import { useMemo } from "react";
import { ListFilter, X } from "lucide-react";
import { ActionButton, Field, SelectInput, TextInput } from "./common";

const FILTER_FIELDS = [
  { key: "asset_type", label: "Type", placeholder: "All types", testId: "asset-filter-type" },
  { key: "operation_status", label: "Operational Status", placeholder: "All statuses", testId: "asset-filter-status" },
  { key: "department", label: "Department", placeholder: "All departments", testId: "asset-filter-department" },
  { key: "location", label: "Location", placeholder: "All locations", testId: "asset-filter-location" },
];
export const EMPTY_ASSET_FILTERS = { search: "", ...Object.fromEntries(FILTER_FIELDS.map(({ key }) => [key, ""])) };
const normalize = (value) => String(value ?? "").trim().toLocaleLowerCase();
// Prefix populated values so a real field value cannot collide with the blank-field option.
const optionValue = (value) => normalize(value) ? `value:${normalize(value)}` : "missing";

export function filterAssets(assets, filters) {
  const search = normalize(filters.search);
  return assets.filter((asset) =>
    (!search || [asset.fixed_asset_id, asset.asset_name].some((value) => normalize(value).includes(search))) &&
    FILTER_FIELDS.every(({ key }) => !filters[key] || optionValue(asset[key]) === filters[key])
  );
}

export const AssetFilters = ({ assets, filters, setFilters, matchCount }) => {
  const options = useMemo(() => Object.fromEntries(FILTER_FIELDS.map(({ key }) => {
    const values = new Map();
    assets.forEach((asset) => {
      const value = optionValue(asset[key]);
      const label = String(asset[key] ?? "").trim().replace(/_/g, " ") || "Not set";
      if (!values.has(value)) values.set(value, key === "operation_status" ? label.replace(/\b[a-z]/g, (letter) => letter.toUpperCase()) : label);
    });
    return [key, [...values].map(([value, label]) => ({ value, label })).sort((a, b) =>
      a.value === "missing" ? 1 : b.value === "missing" ? -1 : a.label.localeCompare(b.label)
    )];
  })), [assets]);
  const hasFilters = Object.values(filters).some(Boolean);
  const setFilter = (key, value) => setFilters((current) => ({ ...current, [key]: value }));

  return (
    <section aria-label="Asset filters" data-testid="asset-filter-controls" className="mb-4 rounded-lg border border-slate-700/60 bg-card p-4">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-slate-400">
          <ListFilter size={16} aria-hidden="true" className="shrink-0 text-amber-500" />
          <p className="text-sm" role="status" aria-live="polite" aria-atomic="true" data-testid="asset-filter-count">
            Showing <span className="font-semibold text-slate-100">{matchCount}</span> of {assets.length} assets
          </p>
        </div>
        <ActionButton type="button" testId="asset-clear-filters" variant="secondary" disabled={!hasFilters} onClick={() => setFilters(EMPTY_ASSET_FILTERS)}>
          <X size={14} aria-hidden="true" /> Clear Filters
        </ActionButton>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
        <div className="min-w-0 sm:col-span-2 lg:col-span-4 xl:col-span-2">
          <Field label="Asset ID / Name">
            <TextInput type="search" testId="asset-filter-search" value={filters.search} onChange={(event) => setFilter("search", event.target.value)} placeholder="Search ID or name…" autoComplete="off" />
          </Field>
        </div>
        {FILTER_FIELDS.map(({ key, label, placeholder, testId }) => (
          <div className="min-w-0" key={key}>
            <Field label={label}>
              <SelectInput testId={testId} value={filters[key]} onChange={(event) => setFilter(key, event.target.value)} options={options[key]} placeholder={placeholder} className="min-w-0 max-w-full" />
            </Field>
          </div>
        ))}
      </div>
    </section>
  );
};
